import React from "react";
import styled, { DefaultTheme } from "styled-components";
import { LucideIcon } from "lucide-react";
import { hexToRGB } from "@shared/utils/misc";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";

export type Tone =
  | "blue"
  | "purple"
  | "green"
  | "red"
  | "amber"
  | "grey"
  | "neutral";

interface IconTileProps {
  icon: LucideIcon;
  tone?: Tone;
  size?: number;
}

/** A lucide icon inside a softly tinted rounded square. */
const IconTile = ({ icon: Icon, tone = "blue", size = 36 }: IconTileProps) => (
  <Tile $tone={tone} $size={size}>
    <Icon size={Math.round(size * 0.5)} strokeWidth={NEW_LOOK_ICON_STROKE} />
  </Tile>
);

export default React.memo(IconTile);

export const toneColor = (theme: DefaultTheme, tone: Tone) =>
  ({
    blue: theme.colors.primaryBlue,
    purple: theme.colors.purple,
    green: theme.colors.green,
    red: theme.colors.red,
    amber: theme.colors.darkYellow,
    grey: theme.colors.secondaryText,
    neutral: theme.colors.secondaryText,
  }[tone]);

export const toneBackground = (theme: DefaultTheme, tone: Tone) => {
  if (tone === "neutral") return theme.colors.cardLightBgColor;
  if (tone === "amber") return hexToRGB(theme.colors.yellow, 0.18);
  if (tone === "grey")
    return hexToRGB(theme.colors.secondaryGrey, theme.isDarkMode ? 0.16 : 0.12);
  return hexToRGB(toneColor(theme, tone), tone === "blue" ? 0.1 : 0.12);
};

const Tile = styled.span<{ $tone: Tone; $size: number }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(p) => (p.$size >= 36 ? 8 : 6)}px;
  color: ${(p) => toneColor(p.theme, p.$tone)};
  background: ${(p) => toneBackground(p.theme, p.$tone)};
`;
